import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import {
  Archive,
  BadgePercent,
  Eye,
  PackagePlus,
  Pencil,
  Plus,
  Save,
  Tags,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/errors'
import {
  useCurrentPermissions,
  useDiscount,
  useDiscountMutations,
  useDiscounts,
  usePriceList,
  usePriceListMutations,
  usePriceLists,
  useProduct,
  useProductMutations,
  useProducts,
} from '@/api/queries'
import type {
  Discount,
  DiscountAppliesTo,
  DiscountKind,
  GymSubscriptionKind,
  PriceList,
  PriceListKind,
  ProductKind,
  ProductListItem,
  VatNature,
} from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { formatBasisPoints, formatDate, formatMoney } from '@/lib/format'
import { moneyInputFromCents, parseMoneyInput } from '@/lib/money'
import { ProductEntitlementsDialog } from '@/features/organization/product-entitlements'

export const Route = createFileRoute('/_app/listini/')({ component: PriceBooksPage })

const PRODUCT_LABELS: Record<ProductKind, string> = {
  subscription: 'Abbonamento',
  service: 'Servizio',
  course: 'Corso',
  goods: 'Prodotto fisico',
  credit: 'Credito',
  digital: 'Digitale',
}
const SUBSCRIPTION_LABELS: Record<GymSubscriptionKind, string> = {
  time: 'A tempo',
  entries: 'A ingressi',
  mixed: 'Misto',
  trial: 'Prova',
}
const PRICE_LIST_LABELS: Record<PriceListKind, string> = {
  base: 'Base',
  promo: 'Promozione',
  segment: 'Segmento',
}

function PriceBooksPage() {
  const permissions = useCurrentPermissions()
  const canWrite = permissions.has('product:write')
  return (
    <>
      <PageHeader
        title="Listini"
        description="Prodotti vendibili, prezzi per listino e sconti. I prezzi sono lordi, IVA inclusa."
      />
      <Tabs variant="pill" defaultValue="price-lists">
        <TabsList>
          <TabsTrigger value="price-lists">
            <Tags /> Listini
          </TabsTrigger>
          <TabsTrigger value="products">
            <Archive /> Prodotti
          </TabsTrigger>
          <TabsTrigger value="discounts">
            <BadgePercent /> Sconti
          </TabsTrigger>
        </TabsList>
        <TabsContent value="price-lists">
          <PriceListsPanel canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="products">
          <ProductsPanel canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="discounts">
          <DiscountsPanel canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </>
  )
}

function ProductsPanel({ canWrite }: { canWrite: boolean }) {
  const list = useProducts({ limit: 100 })
  const mutations = useProductMutations()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ProductListItem | null>(null)
  const [correcting, setCorrecting] = useState<ProductListItem | null>(null)
  const rows = list.data?.data ?? []

  if (list.isError && !list.data)
    return <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          Il prodotto descrive cosa si vende; il prezzo vive nel listino.
        </p>
        {canWrite ? (
          <Button onClick={() => setCreating(true)}>
            <PackagePlus /> Nuovo prodotto
          </Button>
        ) : null}
      </div>
      {list.isPending ? (
        <Skeleton className="h-32" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Archive />}
          title="Catalogo vuoto"
          description="Crea il primo prodotto da mettere a listino."
        />
      ) : (
        <div className="border-border divide-border divide-y rounded-lg border">
          {rows.map((product) => (
            <div key={product.id} className="flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{product.name}</p>
                <p className="text-muted-foreground text-xs">
                  {product.code ? `${product.code} · ` : ''}
                  {PRODUCT_LABELS[product.kind]} · IVA {formatBasisPoints(product.vatRateBp)}
                  {product.subscriptionKind
                    ? ` · ${SUBSCRIPTION_LABELS[product.subscriptionKind]}`
                    : ''}
                  {product.durationDays ? ` · ${product.durationDays} giorni` : ''}
                  {product.entriesTotal ? ` · ${product.entriesTotal} ingressi` : ''}
                </p>
              </div>
              <Badge variant="neutral">{PRODUCT_LABELS[product.kind]}</Badge>
              {/*
                ⚠️ Solo sugli abbonamenti, e non per pigrizia: un prodotto di magazzino non
                «include» niente, e offrire il pulsante ovunque insegnerebbe che una barretta
                proteica può dare accesso alla sauna.
              */}
              {canWrite && product.kind === 'subscription' ? (
                <Button size="sm" variant="outline" onClick={() => setEditing(product)}>
                  Cosa include
                </Button>
              ) : null}
              {canWrite ? (
                <Button size="sm" variant="ghost" onClick={() => setCorrecting(product)}>
                  <Pencil /> Correggi
                </Button>
              ) : null}
              {canWrite ? (
                <Button
                  size="sm"
                  variant="ghost"
                  loading={mutations.archive.isPending}
                  onClick={() => mutations.archive.mutate(product.id)}
                >
                  <Trash2 /> Archivia
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {mutations.archive.isError ? (
        <Alert variant="danger">{errorMessage(mutations.archive.error)}</Alert>
      ) : null}
      <ProductDialog open={creating} onOpenChange={setCreating} />
      {correcting === null ? null : (
        <ProductEditDialog product={correcting} onClose={() => setCorrecting(null)} />
      )}
      {editing === null ? null : (
        <ProductEntitlementsDialog
          productId={editing.id}
          productName={editing.name}
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        />
      )}
    </div>
  )
}

/**
 * Correggere un prodotto già creato — `PATCH /organization/products/{id}`.
 *
 * ## Il tipo non si cambia, e non è una limitazione dell'interfaccia
 *
 * `ProductPatch` è `Partial<Omit<ProductInput, 'kind'>>`: il contract toglie `kind`
 * deliberatamente. Un abbonamento diventato «prodotto fisico» renderebbe senza senso le
 * iscrizioni che ne sono nate, e le righe di vendita che lo nominano porterebbero un
 * tipo che non spiega più cosa è stato venduto. Chi ha sbagliato tipo archivia e ricrea.
 *
 * ## Rilegge il dettaglio per l'`ETag`
 *
 * Come per lo sconto: l'elenco non porta `ETag` per riga, quindi `useProduct` va a
 * prenderlo mentre il form nasce già compilato dai valori della riga — si può scrivere
 * subito, e l'`If-Match` si aggiunge quando arriva. Se il backend non espone l'header
 * in CORS resta `null` e la modifica procede senza lucchetto, che è degradare e non
 * rompere.
 */
function ProductEditDialog({
  product,
  onClose,
}: {
  product: ProductListItem
  onClose: () => void
}) {
  const existing = useProduct(product.id)
  const { update } = useProductMutations()
  const [name, setName] = useState(product.name)
  const [code, setCode] = useState(product.code ?? '')
  const [description, setDescription] = useState('')
  const [subscriptionKind, setSubscriptionKind] = useState<GymSubscriptionKind>(
    product.subscriptionKind ?? 'time',
  )
  const [durationDays, setDurationDays] = useState(
    product.durationDays === null ? '' : String(product.durationDays),
  )
  const [entriesTotal, setEntriesTotal] = useState(
    product.entriesTotal === null ? '' : String(product.entriesTotal),
  )
  const [vatNature, setVatNature] = useState<VatNature>('taxable')
  const [vatRate, setVatRate] = useState(String(product.vatRateBp / 100))
  const [error, setError] = useState<string | null>(null)
  const [adopted, setAdopted] = useState(false)

  // Descrizione e natura IVA **non** sono nell'elenco: arrivano solo dal dettaglio. Si
  // adottano una volta sola, così quello che l'operatore sta scrivendo non viene
  // sovrascritto da una rilettura — e finché non arrivano il form è già compilabile
  // sugli altri campi, che dall'elenco ci sono tutti.
  const loaded = existing.data?.data
  if (!adopted && loaded !== undefined) {
    setAdopted(true)
    setDescription(loaded.description ?? '')
    setVatNature(loaded.vatNature)
  }

  const isSubscription = product.kind === 'subscription'
  const needsDuration =
    isSubscription && (subscriptionKind === 'time' || subscriptionKind === 'trial')
  const needsEntries =
    isSubscription && (subscriptionKind === 'entries' || subscriptionKind === 'mixed')

  async function submit() {
    if (name.trim() === '') {
      setError('Il nome è obbligatorio.')
      return
    }
    setError(null)
    try {
      await update.mutateAsync({
        id: product.id,
        etag: existing.data?.etag ?? null,
        input: {
          name: name.trim(),
          code: code.trim() === '' ? null : code.trim(),
          description: description.trim() === '' ? null : description.trim(),
          vatNature,
          vatRateBp:
            vatNature === 'taxable' ? Math.round(Number(vatRate.replace(',', '.')) * 100) : 0,
          ...(isSubscription
            ? {
                subscriptionKind,
                durationDays: durationDays === '' ? null : Number(durationDays),
                entriesTotal: entriesTotal === '' ? null : Number(entriesTotal),
              }
            : {}),
        },
      })
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Correggi prodotto</DialogTitle>
          <DialogDescription>
            {PRODUCT_LABELS[product.kind]} — il tipo non si cambia: le vendite già emesse
            porterebbero un prodotto che non spiega più cosa è stato venduto. Il prezzo sta nel
            listino, non qui.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome" required className="sm:col-span-2">
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Codice SKU" hint="Vuoto: nessun codice.">
            {(props) => <Input {...props} value={code} onChange={(e) => setCode(e.target.value)} />}
          </Field>
          {isSubscription ? (
            <>
              <Field label="Modello iscrizione">
                {(props) => (
                  <Select
                    {...props}
                    value={subscriptionKind}
                    onValueChange={(value) => setSubscriptionKind(value as typeof subscriptionKind)}
                    items={Object.entries(SUBSCRIPTION_LABELS).map(([value, label]) => ({
                      value: value as GymSubscriptionKind,
                      label,
                    }))}
                  />
                )}
              </Field>
              {needsDuration || subscriptionKind === 'mixed' ? (
                <Field label="Durata in giorni" required={needsDuration}>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={1}
                      value={durationDays}
                      onChange={(e) => setDurationDays(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
              {needsEntries ? (
                <Field label="Numero ingressi" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={1}
                      value={entriesTotal}
                      onChange={(e) => setEntriesTotal(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
            </>
          ) : null}
          <Field label="Natura IVA">
            {(props) => (
              <Select
                {...props}
                value={vatNature}
                onValueChange={(value) => setVatNature(value as typeof vatNature)}
                items={[
                  { value: 'taxable', label: 'Imponibile' },
                  { value: 'exempt_art10', label: 'Esente art. 10' },
                  { value: 'not_subject', label: 'Non soggetta' },
                  { value: 'out_of_scope', label: 'Fuori campo' },
                ]}
              />
            )}
          </Field>
          {vatNature === 'taxable' ? (
            <Field label="Aliquota IVA %">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                />
              )}
            </Field>
          ) : null}
          <Field label="Descrizione" className="sm:col-span-2">
            {(props) => (
              <Textarea
                {...props}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
          {existing.data?.etag === null && !existing.isPending ? (
            <Alert variant="warning" className="sm:col-span-2">
              Il server non espone l'<code className="font-mono">ETag</code>: se un collega sta
              correggendo lo stesso prodotto, vince chi salva per ultimo.
            </Alert>
          ) : null}
          {error !== null ? (
            <Alert variant="danger" className="sm:col-span-2">
              {error}
            </Alert>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annulla
          </Button>
          <Button
            loading={update.isPending}
            disabled={
              name.trim() === '' ||
              (needsDuration && durationDays === '') ||
              (needsEntries && entriesTotal === '')
            }
            onClick={() => void submit()}
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ProductDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { create } = useProductMutations()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [kind, setKind] = useState<ProductKind>('subscription')
  const [subscriptionKind, setSubscriptionKind] = useState<GymSubscriptionKind>('time')
  const [durationDays, setDurationDays] = useState('30')
  const [entriesTotal, setEntriesTotal] = useState('')
  const [vatNature, setVatNature] = useState<VatNature>('taxable')
  const [vatRate, setVatRate] = useState('22')
  const needsDuration =
    kind === 'subscription' && (subscriptionKind === 'time' || subscriptionKind === 'trial')
  const needsEntries =
    kind === 'subscription' && (subscriptionKind === 'entries' || subscriptionKind === 'mixed')

  async function submit() {
    if (name.trim() === '') return
    await create.mutateAsync({
      name: name.trim(),
      kind,
      ...(code.trim() ? { code: code.trim() } : {}),
      ...(description.trim() ? { description: description.trim() } : {}),
      vatNature,
      vatRateBp: vatNature === 'taxable' ? Math.round(Number(vatRate.replace(',', '.')) * 100) : 0,
      ...(kind === 'subscription'
        ? {
            subscriptionKind,
            ...(durationDays ? { durationDays: Number(durationDays) } : {}),
            ...(entriesTotal ? { entriesTotal: Number(entriesTotal) } : {}),
          }
        : {}),
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuovo prodotto</DialogTitle>
          <DialogDescription>Il prezzo si aggiunge dopo, in ciascun listino.</DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome" required className="sm:col-span-2">
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Codice SKU">
            {(props) => <Input {...props} value={code} onChange={(e) => setCode(e.target.value)} />}
          </Field>
          <Field label="Tipo">
            {(props) => (
              <Select
                {...props}
                value={kind}
                onValueChange={(value) => setKind(value as typeof kind)}
                items={Object.entries(PRODUCT_LABELS).map(([value, label]) => ({
                  value: value as ProductKind,
                  label,
                }))}
              />
            )}
          </Field>
          {kind === 'subscription' ? (
            <>
              <Field label="Modello iscrizione">
                {(props) => (
                  <Select
                    {...props}
                    value={subscriptionKind}
                    onValueChange={(value) => setSubscriptionKind(value as typeof subscriptionKind)}
                    items={Object.entries(SUBSCRIPTION_LABELS).map(([value, label]) => ({
                      value: value as GymSubscriptionKind,
                      label,
                    }))}
                  />
                )}
              </Field>
              {needsDuration || subscriptionKind === 'mixed' ? (
                <Field label="Durata in giorni" required={needsDuration}>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={1}
                      value={durationDays}
                      onChange={(e) => setDurationDays(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
              {needsEntries ? (
                <Field label="Numero ingressi" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={1}
                      value={entriesTotal}
                      onChange={(e) => setEntriesTotal(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
            </>
          ) : null}
          <Field label="Natura IVA">
            {(props) => (
              <Select
                {...props}
                value={vatNature}
                onValueChange={(value) => setVatNature(value as typeof vatNature)}
                items={[
                  { value: 'taxable', label: 'Imponibile' },
                  { value: 'exempt_art10', label: 'Esente art. 10' },
                  { value: 'not_subject', label: 'Non soggetta' },
                  { value: 'out_of_scope', label: 'Fuori campo' },
                ]}
              />
            )}
          </Field>
          {vatNature === 'taxable' ? (
            <Field label="Aliquota IVA %">
              {(props) => (
                <Input
                  {...props}
                  inputMode="decimal"
                  value={vatRate}
                  onChange={(e) => setVatRate(e.target.value)}
                />
              )}
            </Field>
          ) : null}
          <Field label="Descrizione" className="sm:col-span-2">
            {(props) => (
              <Textarea
                {...props}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
          {create.isError ? (
            <Alert variant="danger" className="sm:col-span-2">
              {errorMessage(create.error)}
            </Alert>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            loading={create.isPending}
            disabled={
              name.trim() === '' ||
              (needsDuration && durationDays === '') ||
              (needsEntries && entriesTotal === '')
            }
            onClick={() => void submit()}
          >
            Crea prodotto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PriceListsPanel({ canWrite }: { canWrite: boolean }) {
  const lists = usePriceLists({ limit: 100 })
  const [selectedId, setSelectedId] = useState('')
  const [creating, setCreating] = useState(false)
  const activeId = selectedId || lists.data?.data[0]?.id || ''
  const detail = usePriceList(activeId || null)
  const products = useProducts({ ...(activeId ? { priceListId: activeId } : {}), limit: 100 })
  const mutations = usePriceListMutations()
  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState(false)
  const [editingHeader, setEditingHeader] = useState(false)

  if (lists.isError && !lists.data)
    return <ErrorState message={errorMessage(lists.error)} onRetry={() => void lists.refetch()} />
  const rows = products.data?.data ?? []
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <Field label="Listino da compilare">
          {(props) => (
            <Select
              {...props}
              value={activeId || undefined}
              onValueChange={(value) => {
                setSelectedId(value)
                setDraftPrices({})
              }}
              placeholder="Nessun listino"
              items={(lists.data?.data ?? []).map((list) => ({
                value: list.id,
                label: (
                  <span className="inline-flex items-center gap-2">
                    {list.name}
                    <span className="text-muted-foreground text-xs">{`${list.itemCount} prezzi`}</span>
                  </span>
                ),
              }))}
            />
          )}
        </Field>
        <div className="flex items-end gap-2">
          <Button variant="outline" disabled={!activeId} onClick={() => setPreview(!preview)}>
            <Eye /> {preview ? 'Compila' : 'Anteprima cliente'}
          </Button>
          {canWrite ? (
            <Button variant="outline" disabled={!activeId} onClick={() => setEditingHeader(true)}>
              <Pencil /> Intestazione
            </Button>
          ) : null}
        </div>
        {canWrite ? (
          <div className="flex items-end">
            <Button onClick={() => setCreating(true)}>
              <Plus /> Nuovo listino
            </Button>
          </div>
        ) : null}
      </div>

      {activeId === '' ? (
        <EmptyState
          icon={<Tags />}
          title="Nessun listino"
          description="Crea il listino base, poi assegna un prezzo ai prodotti."
        />
      ) : products.isPending ? (
        <Skeleton className="h-40" />
      ) : preview ? (
        <CustomerPreview name={detail.data?.data.name ?? 'Listino'} rows={rows} />
      ) : (
        <div className="border-border divide-border divide-y rounded-lg border">
          {rows.map((product) => {
            const value =
              draftPrices[product.id] ??
              (product.priceCents === null ? '' : moneyInputFromCents(product.priceCents))
            const cents = value === '' ? null : parseMoneyInput(value)
            return (
              <div
                key={product.id}
                className="grid items-center gap-2 p-3 sm:grid-cols-[1fr_10rem_auto]"
              >
                <div>
                  <p className="font-medium">{product.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {PRODUCT_LABELS[product.kind]} · IVA {formatBasisPoints(product.vatRateBp)}
                  </p>
                </div>
                <Input
                  aria-label={`Prezzo ${product.name}`}
                  inputMode="decimal"
                  placeholder="0,00"
                  value={value}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setDraftPrices((current) => ({ ...current, [product.id]: e.target.value }))
                  }
                />
                {canWrite ? (
                  <div className="flex gap-1">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label="Salva prezzo"
                      disabled={
                        cents === null ||
                        (product.priceCents !== null && cents === product.priceCents)
                      }
                      onClick={() =>
                        mutations.setPrice.mutate({
                          id: activeId,
                          productId: product.id,
                          priceCents: cents as number,
                          etag: detail.data?.etag,
                        })
                      }
                    >
                      <Save />
                    </Button>
                    {product.priceCents !== null ? (
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        aria-label="Rimuovi prezzo"
                        onClick={() =>
                          mutations.removePrice.mutate({
                            id: activeId,
                            productId: product.id,
                            etag: detail.data?.etag,
                          })
                        }
                      >
                        <Trash2 />
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
      {mutations.setPrice.isError || mutations.removePrice.isError ? (
        <Alert variant="danger">
          {errorMessage(mutations.setPrice.error ?? mutations.removePrice.error)}
        </Alert>
      ) : null}
      <PriceListDialog open={creating} onOpenChange={setCreating} />
      {editingHeader && detail.data !== undefined ? (
        <PriceListEditDialog
          priceList={detail.data.data}
          etag={detail.data.etag}
          onClose={() => setEditingHeader(false)}
        />
      ) : null}
    </div>
  )
}

function CustomerPreview({ name, rows }: { name: string; rows: ProductListItem[] }) {
  const priced = rows.filter((row) => row.priceCents !== null)
  return (
    <section className="border-border bg-card rounded-xl border p-5">
      <div className="mb-4">
        <Badge variant="neutral">Anteprima cliente</Badge>
        <h2 className="mt-2 text-xl font-semibold">{name}</h2>
        <p className="text-muted-foreground text-sm">Prezzi IVA inclusa.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {priced.map((product) => (
          <article key={product.id} className="border-border rounded-lg border p-4">
            <p className="font-semibold">{product.name}</p>
            <p className="text-muted-foreground mt-1 text-xs">
              {PRODUCT_LABELS[product.kind]}
              {product.durationDays ? ` · ${product.durationDays} giorni` : ''}
              {product.entriesTotal ? ` · ${product.entriesTotal} ingressi` : ''}
            </p>
            <p className="mt-4 text-2xl font-semibold" data-numeric>
              {formatMoney(product.priceCents)}
            </p>
          </article>
        ))}
      </div>
      {priced.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          Questo listino non ha ancora prezzi visibili.
        </p>
      ) : null}
    </section>
  )
}

/**
 * L'intestazione di un listino — `PATCH /organization/price-lists/{id}`.
 *
 * ## Cosa si cambia
 *
 * Nome, tipo, validità, priorità e sede. **Non la valuta**: il contract la toglie dalla
 * `PATCH`, perché gli importi già scritti resterebbero numeri interpretati in una valuta
 * che nessuno ha ricontrollato — un listino che cambia valuta è un listino nuovo.
 *
 * I prezzi non stanno qui: si compilano riga per riga nella pagina, ognuno con il suo
 * `PUT`. Questa finestra tocca **la copertina**, non il contenuto.
 *
 * ## L'`ETag` c'è già
 *
 * La pagina legge comunque il dettaglio del listino attivo — le serve il nome per
 * l'anteprima cliente —, quindi l'`If-Match` arriva senza una richiesta in più: è il caso
 * in cui il lucchetto costa zero, e sarebbe assurdo non usarlo.
 */
function PriceListEditDialog({
  priceList,
  etag,
  onClose,
}: {
  priceList: PriceList
  etag: string | null
  onClose: () => void
}) {
  const { update } = usePriceListMutations()
  const [name, setName] = useState(priceList.name)
  const [kind, setKind] = useState<PriceListKind>(priceList.kind)
  const [validFrom, setValidFrom] = useState(priceList.validFrom ?? '')
  const [validTo, setValidTo] = useState(priceList.validTo ?? '')
  const [priority, setPriority] = useState(String(priceList.priority))
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    if (name.trim() === '') {
      setError('Il nome è obbligatorio.')
      return
    }
    if (validFrom !== '' && validTo !== '' && validTo < validFrom) {
      setError('La fine della validità non può precedere l’inizio.')
      return
    }
    setError(null)
    try {
      await update.mutateAsync({
        id: priceList.id,
        etag,
        input: {
          name: name.trim(),
          kind,
          validFrom: validFrom === '' ? null : validFrom,
          validTo: validTo === '' ? null : validTo,
          priority: Number(priority),
        },
      })
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Intestazione del listino</DialogTitle>
          <DialogDescription>
            {priceList.itemCount} prezzi, in {priceList.currency}. La valuta non si cambia: gli
            importi già scritti resterebbero numeri in una valuta che nessuno ha ricontrollato.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome" required className="sm:col-span-2">
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Tipo">
            {(props) => (
              <Select
                {...props}
                value={kind}
                onValueChange={(value) => setKind(value as typeof kind)}
                items={Object.entries(PRICE_LIST_LABELS).map(([value, label]) => ({
                  value: value as PriceListKind,
                  label,
                }))}
              />
            )}
          </Field>
          <Field label="Priorità" hint="A parità di validità vince il numero più alto.">
            {(props) => (
              <Input
                {...props}
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            )}
          </Field>
          <Field label="Valido dal" hint="Vuoto: da sempre.">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            )}
          </Field>
          <Field label="Valido al" hint="Vuoto: senza fine.">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
              />
            )}
          </Field>
          {etag === null ? (
            <Alert variant="warning" className="sm:col-span-2">
              Il server non espone l'<code className="font-mono">ETag</code>: vince chi salva per
              ultimo.
            </Alert>
          ) : null}
          {error !== null ? (
            <Alert variant="danger" className="sm:col-span-2">
              {error}
            </Alert>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annulla
          </Button>
          <Button
            loading={update.isPending}
            disabled={name.trim() === ''}
            onClick={() => void submit()}
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PriceListDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { create } = usePriceListMutations()
  const [name, setName] = useState('')
  const [kind, setKind] = useState<PriceListKind>('base')
  const [validFrom, setValidFrom] = useState('')
  const [validTo, setValidTo] = useState('')
  const [priority, setPriority] = useState('0')
  async function submit() {
    await create.mutateAsync({
      name: name.trim(),
      kind,
      ...(validFrom ? { validFrom } : {}),
      ...(validTo ? { validTo } : {}),
      priority: Number(priority),
    })
    onOpenChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuovo listino</DialogTitle>
          <DialogDescription>
            Nasce vuoto: i prezzi si compilano prodotto per prodotto.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome" required className="sm:col-span-2">
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Tipo">
            {(props) => (
              <Select
                {...props}
                value={kind}
                onValueChange={(value) => setKind(value as typeof kind)}
                items={Object.entries(PRICE_LIST_LABELS).map(([value, label]) => ({
                  value: value as PriceListKind,
                  label,
                }))}
              />
            )}
          </Field>
          <Field label="Priorità">
            {(props) => (
              <Input
                {...props}
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            )}
          </Field>
          <Field label="Valido dal">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            )}
          </Field>
          <Field label="Valido al">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
              />
            )}
          </Field>
          {create.isError ? (
            <Alert variant="danger" className="sm:col-span-2">
              {errorMessage(create.error)}
            </Alert>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            loading={create.isPending}
            disabled={name.trim() === ''}
            onClick={() => void submit()}
          >
            Crea listino
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DiscountsPanel({ canWrite }: { canWrite: boolean }) {
  const list = useDiscounts({ limit: 100 })
  const mutations = useDiscountMutations()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Discount | null>(null)
  const rows = list.data?.data ?? []
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">
          Coupon e sconti con limiti verificati dal database al momento del riscatto.
        </p>
        {canWrite ? (
          <Button onClick={() => setCreating(true)}>
            <Plus /> Nuovo sconto
          </Button>
        ) : null}
      </div>
      {list.isPending ? (
        <Skeleton className="h-32" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BadgePercent />}
          title="Nessuno sconto"
          description="Crea una promozione o un coupon."
        />
      ) : (
        <div className="border-border divide-border divide-y rounded-lg border">
          {rows.map((discount) => (
            <div key={discount.id} className="flex flex-wrap items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{discount.name}</p>
                <p className="text-muted-foreground text-xs">
                  {discount.kind === 'percent'
                    ? formatBasisPoints(discount.percentBp)
                    : formatMoney(discount.amountCents)}{' '}
                  · {discount.appliesTo === 'sale' ? 'sul totale' : 'sulla riga'}
                  {discount.code ? ` · codice ${discount.code}` : ''}
                  {discount.validTo ? ` · fino al ${formatDate(discount.validTo)}` : ''} ·{' '}
                  {discount.redemptionsUsed} utilizzi
                </p>
              </div>
              {discount.maxRedemptions !== null ? (
                <Badge variant="neutral">
                  {discount.redemptionsUsed}/{discount.maxRedemptions}
                </Badge>
              ) : null}
              {canWrite ? (
                <Button size="sm" variant="outline" onClick={() => setEditing(discount)}>
                  <Pencil /> Modifica
                </Button>
              ) : null}
              {canWrite ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => mutations.archive.mutate(discount.id)}
                >
                  <Trash2 /> Archivia
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {mutations.archive.isError ? (
        <Alert variant="danger">{errorMessage(mutations.archive.error)}</Alert>
      ) : null}
      <DiscountDialog open={creating} onOpenChange={setCreating} />
      {editing === null ? null : (
        <DiscountEditDialog discount={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  )
}

function DiscountDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { create } = useDiscountMutations()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [kind, setKind] = useState<DiscountKind>('percent')
  const [value, setValue] = useState('10')
  const [appliesTo, setAppliesTo] = useState<DiscountAppliesTo>('sale')
  const [validTo, setValidTo] = useState('')
  const [maxRedemptions, setMaxRedemptions] = useState('')
  async function submit() {
    const numeric =
      kind === 'percent'
        ? Math.round(Number(value.replace(',', '.')) * 100)
        : parseMoneyInput(value)
    if (numeric === null || numeric <= 0) return
    await create.mutateAsync({
      name: name.trim(),
      kind,
      appliesTo,
      ...(code.trim() ? { code: code.trim() } : {}),
      ...(kind === 'percent' ? { percentBp: numeric } : { amountCents: numeric }),
      ...(validTo ? { validTo } : {}),
      ...(maxRedemptions ? { maxRedemptions: Number(maxRedemptions) } : {}),
    })
    onOpenChange(false)
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuovo sconto</DialogTitle>
          <DialogDescription>
            Tipo, valore e ambito non si cambiano più: si archivia e si ricrea. Nome, finestra di
            validità e tetti restano correggibili.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label="Codice coupon">
            {(props) => (
              <Input
                {...props}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Field label="Tipo">
            {(props) => (
              <Select
                {...props}
                value={kind}
                onValueChange={(value) => setKind(value as typeof kind)}
                items={[
                  { value: 'percent', label: 'Percentuale' },
                  { value: 'amount', label: 'Importo fisso' },
                ]}
              />
            )}
          </Field>
          <Field label={kind === 'percent' ? 'Percentuale %' : 'Importo €'} required>
            {(props) => (
              <Input
                {...props}
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            )}
          </Field>
          <Field label="Applica">
            {(props) => (
              <Select
                {...props}
                value={appliesTo}
                onValueChange={(value) => setAppliesTo(value as typeof appliesTo)}
                items={[
                  { value: 'sale', label: 'Al totale vendita' },
                  { value: 'line', label: 'A una riga' },
                ]}
              />
            )}
          </Field>
          <Field label="Valido fino al">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
              />
            )}
          </Field>
          <Field label="Numero massimo utilizzi">
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
              />
            )}
          </Field>
          {create.isError ? (
            <Alert variant="danger" className="sm:col-span-2">
              {errorMessage(create.error)}
            </Alert>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            loading={create.isPending}
            disabled={name.trim() === '' || value === ''}
            onClick={() => void submit()}
          >
            Crea sconto
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Correggere uno sconto già creato — la seconda delle due rotte che l'audit del 20/8
 * ha trovato senza chiamanti (§3.10 del piano).
 *
 * Perché un dialogo a parte invece di riusare quello di creazione: i campi che si
 * possono correggere sono un **sottoinsieme stretto**, e la ragione è di sostanza —
 * le righe di vendita portano l'abbuono in centesimi, non la regola. Un dialogo unico
 * con metà dei campi disabilitati insegnerebbe che quella metà è una scelta di
 * interfaccia; qui la regola si mostra e non si tocca.
 *
 * ⚠️ Rilegge il singolo sconto per avere il suo `ETag` (`useDiscount`), e nel frattempo
 * mostra i valori della riga d'elenco: il form nasce da quelli, così si scrive anche
 * prima che la rilettura arrivi, e l'`If-Match` si aggiunge se c'è.
 */
function DiscountEditDialog({ discount, onClose }: { discount: Discount; onClose: () => void }) {
  const existing = useDiscount(discount.id)
  const { update } = useDiscountMutations()
  const [name, setName] = useState(discount.name)
  const [code, setCode] = useState(discount.code ?? '')
  const [validFrom, setValidFrom] = useState(discount.validFrom ?? '')
  const [validTo, setValidTo] = useState(discount.validTo ?? '')
  const [maxRedemptions, setMaxRedemptions] = useState(
    discount.maxRedemptions === null ? '' : String(discount.maxRedemptions),
  )
  const [perMemberLimit, setPerMemberLimit] = useState(
    discount.perMemberLimit === null ? '' : String(discount.perMemberLimit),
  )
  const [error, setError] = useState<string | null>(null)

  const cap = maxRedemptions.trim() === '' ? null : Number(maxRedemptions)
  // Il server risponde `422` comunque, ma dirlo qui risparmia un giro e spiega il perché.
  const capTooLow = cap !== null && cap < discount.redemptionsUsed

  async function submit() {
    if (name.trim() === '') {
      setError('Il nome è obbligatorio.')
      return
    }
    if (validFrom !== '' && validTo !== '' && validTo < validFrom) {
      setError('La fine della validità non può precedere l’inizio.')
      return
    }
    if (capTooLow) {
      setError(
        `Lo sconto è già stato usato ${discount.redemptionsUsed} volte: il tetto non può scendere sotto.`,
      )
      return
    }
    setError(null)
    try {
      await update.mutateAsync({
        id: discount.id,
        etag: existing.data?.etag ?? null,
        input: {
          name: name.trim(),
          code: code.trim() === '' ? null : code.trim(),
          validFrom: validFrom === '' ? null : validFrom,
          validTo: validTo === '' ? null : validTo,
          maxRedemptions: cap,
          perMemberLimit: perMemberLimit.trim() === '' ? null : Number(perMemberLimit),
        },
      })
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifica sconto</DialogTitle>
          <DialogDescription>
            {discount.kind === 'percent'
              ? formatBasisPoints(discount.percentBp)
              : formatMoney(discount.amountCents)}{' '}
            {discount.appliesTo === 'sale' ? 'sul totale' : 'sulla riga'} — tipo, valore e ambito
            non si cambiano: quelle vendite porterebbero un abbuono senza più la sua regola.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome" required className="sm:col-span-2">
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field
            label="Codice coupon"
            hint="Vuoto: sconto deciso dall’operatore, niente da digitare."
          >
            {(props) => (
              <Input
                {...props}
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Field label="Valido dal">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
              />
            )}
          </Field>
          <Field label="Valido fino al">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={validTo}
                onChange={(e) => setValidTo(e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Numero massimo utilizzi"
            hint={`Già usato ${discount.redemptionsUsed} volte. Vuoto: senza limite.`}
          >
            {(props) => (
              <Input
                {...props}
                type="number"
                min={discount.redemptionsUsed > 0 ? discount.redemptionsUsed : 1}
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
              />
            )}
          </Field>
          <Field label="Utilizzi per persona" hint="Non vale per le vendite senza intestatario.">
            {(props) => (
              <Input
                {...props}
                type="number"
                min={1}
                value={perMemberLimit}
                onChange={(e) => setPerMemberLimit(e.target.value)}
              />
            )}
          </Field>
          {error === null ? null : (
            <Alert variant="danger" className="sm:col-span-2">
              {error}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annulla
          </Button>
          <Button
            loading={update.isPending}
            disabled={name.trim() === '' || capTooLow}
            onClick={() => void submit()}
          >
            <Save /> Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
